import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fabricImg, fmtPrice } from "@/lib/designOptions";

const STATUS_STYLES = {
  new: "border-sky-500/40 text-sky-400",
  contacted: "border-[#C59B27]/50 text-[#C59B27]",
  "in-production": "border-emerald-500/40 text-emerald-400",
  closed: "border-stone-500/40 text-stone-400",
};

function Action({ testid, onClick, label, accent }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors active:scale-95 ${
        accent
          ? "border-[#C59B27] text-[#C59B27] hover:bg-[#C59B27] hover:text-[#121110]"
          : "border-[#2A2825] text-[#858078] hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
      }`}
    >
      {label}
    </button>
  );
}

export function DesignsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/design-requests");
      setRequests(data.requests);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (requestId, status) => {
    try {
      await api.patch(`/admin/design-requests/${requestId}`, { status });
      toast.success(`Design marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-10" data-testid="admin-designs-tab">
      <p className="max-w-xl text-sm font-light leading-relaxed text-[#858078]">
        Design Studio requests — clients who designed their own garment online. Confirm cloth and
        pricing, then move each one along.
      </p>
      <div className="mt-8 space-y-3" data-testid="admin-designs-list">
        {loading && <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>}
        {!loading && requests.length === 0 && (
          <p className="border border-[#2A2825] bg-[#1A1917] py-14 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]" data-testid="admin-designs-empty">
            No design requests yet
          </p>
        )}
        {!loading &&
          requests.map((r) => (
            <div
              key={r.request_id}
              data-testid={`admin-design-${r.request_id}`}
              className="grid gap-4 border border-[#2A2825] bg-[#1A1917] p-6 lg:grid-cols-12 lg:items-center"
            >
              <div className="lg:col-span-3">
                <p className="font-serif text-lg text-[#FFFFFF]">{r.name}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#C59B27]">{r.request_id}</p>
                <p className="mt-1 text-xs text-[#858078]">{r.email} · {r.phone}</p>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm text-[#FFFFFF]">{r.garment_name} · {r.fabric_name} ({r.mill})</p>
                <p className="mt-1 text-xs text-[#858078]">
                  {Object.entries(r.options || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "No options"}
                </p>
                {r.estimated_from && (
                  <p className="mt-1 text-xs text-[#C59B27]">Estimated from {fmtPrice(r.estimated_from)}</p>
                )}
                {r.notes && <p className="mt-1 text-xs italic text-[#858078]">"{r.notes}"</p>}
              </div>
              <div className="lg:col-span-2">
                <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[r.status]}`}>
                  {r.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                {r.status === "new" && (
                  <Action testid={`admin-contact-design-${r.request_id}`} onClick={() => setStatus(r.request_id, "contacted")} label="Mark Contacted" accent />
                )}
                {r.status === "contacted" && (
                  <Action testid={`admin-production-design-${r.request_id}`} onClick={() => setStatus(r.request_id, "in-production")} label="In Production" accent />
                )}
                {r.status !== "closed" && (
                  <Action testid={`admin-close-design-${r.request_id}`} onClick={() => setStatus(r.request_id, "closed")} label="Close" />
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

const EMPTY_FABRIC = { name: "", mill: "", color: "", pattern: "Solid", composition: "100% Wool", price_from: "" };
const fabricInputCls =
  "w-full border border-[#2A2825] bg-[#121110] px-3 py-2.5 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]";

export function FabricsTab() {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FABRIC);
  const [showAdd, setShowAdd] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [priceDrafts, setPriceDrafts] = useState({});
  const fileRef = useRef(null);
  const targetRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/fabrics");
      setFabrics(data.fabrics);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pickFile = (fabricId) => {
    targetRef.current = fabricId;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const fabricId = targetRef.current;
    if (!file || !fabricId) return;
    setUploadingId(fabricId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/fabrics/${fabricId}/image`, fd);
      toast.success("Swatch photo updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingId(null);
    }
  };

  const toggleActive = async (f) => {
    try {
      await api.patch(`/admin/fabrics/${f.fabric_id}`, { active: !f.active });
      toast.success(f.active ? "Fabric hidden from the studio." : "Fabric visible in the studio.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addFabric = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/fabrics", { ...form, price_from: Number(form.price_from) || null });
      toast.success("Fabric added — now upload its swatch photo.");
      setForm(EMPTY_FABRIC);
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePrice = async (f) => {
    const value = Number(priceDrafts[f.fabric_id]);
    if (!value || value === f.price_from) return;
    try {
      await api.patch(`/admin/fabrics/${f.fabric_id}`, { price_from: value });
      toast.success(`${f.name} — suit now from ${fmtPrice(value)}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mt-10" data-testid="admin-fabrics-tab">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="admin-fabric-file-input" />
      <StudioVideoPanel />
      <div className="mt-10 flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-xl text-sm font-light leading-relaxed text-[#858078]">
          The cloth library behind the Design Studio. Upload your real swatch photos to replace the
          placeholders — clients see changes instantly.
        </p>
        <button
          data-testid="admin-add-fabric-toggle"
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-2 bg-[#C59B27] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#121110] transition-colors hover:bg-[#FFFFFF] active:scale-95"
        >
          <Plus size={13} /> {showAdd ? "Cancel" : "Add Fabric"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addFabric} className="mt-6 grid gap-4 border border-[#2A2825] bg-[#1A1917] p-6 sm:grid-cols-2 lg:grid-cols-5" data-testid="admin-add-fabric-form">
          <input data-testid="admin-fabric-name-input" required placeholder="Name (Navy Twill…)" value={form.name} onChange={setF("name")} className={fabricInputCls} />
          <input data-testid="admin-fabric-mill-input" required placeholder="Mill (Loro Piana…)" value={form.mill} onChange={setF("mill")} className={fabricInputCls} />
          <input data-testid="admin-fabric-color-input" required placeholder="Color (Dark Blue…)" value={form.color} onChange={setF("color")} className={fabricInputCls} />
          <input data-testid="admin-fabric-pattern-input" placeholder="Pattern (Solid…)" value={form.pattern} onChange={setF("pattern")} className={fabricInputCls} />
          <div className="flex gap-2">
            <input data-testid="admin-fabric-composition-input" placeholder="Composition" value={form.composition} onChange={setF("composition")} className={fabricInputCls} />
            <input data-testid="admin-fabric-price-input" type="number" placeholder="Suit from $" value={form.price_from} onChange={setF("price_from")} className={fabricInputCls} />
            <button data-testid="admin-fabric-save-button" type="submit" className="shrink-0 border border-[#C59B27] px-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C59B27] transition-colors hover:bg-[#C59B27] hover:text-[#121110]">
              Save
            </button>
          </div>
        </form>
      )}

      {loading && <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-fabrics-grid">
        {fabrics.map((f) => (
          <div key={f.fabric_id} data-testid={`admin-fabric-${f.fabric_id}`} className={`border border-[#2A2825] bg-[#1A1917] ${f.active ? "" : "opacity-50"}`}>
            {fabricImg(f) ? (
              <img src={fabricImg(f)} alt={f.name} loading="lazy" className="aspect-square w-full object-cover" />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center bg-[#121110] font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A564F]">
                No swatch photo
              </div>
            )}
            <div className="p-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#C59B27]">{f.mill}</p>
              <p className="mt-1 font-serif text-base text-[#FFFFFF]">{f.name}</p>
              <p className="mt-0.5 text-[11px] text-[#858078]">{f.color} · {f.pattern}{f.has_custom_image ? " · Your photo" : " · Placeholder"}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#858078]">Suit from $</span>
                <input
                  data-testid={`admin-fabric-price-${f.fabric_id}`}
                  type="number"
                  value={priceDrafts[f.fabric_id] ?? f.price_from ?? ""}
                  onChange={(e) => setPriceDrafts({ ...priceDrafts, [f.fabric_id]: e.target.value })}
                  className="w-20 border border-[#2A2825] bg-[#121110] px-2 py-1.5 text-xs text-[#FFFFFF] outline-none focus:border-[#C59B27]"
                />
                <button
                  data-testid={`admin-fabric-price-save-${f.fabric_id}`}
                  onClick={() => savePrice(f)}
                  className="border border-[#2A2825] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#858078] transition-colors hover:border-[#C59B27] hover:text-[#C59B27]"
                >
                  Set
                </button>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid={`admin-fabric-upload-${f.fabric_id}`}
                  onClick={() => pickFile(f.fabric_id)}
                  disabled={uploadingId === f.fabric_id}
                  className="inline-flex flex-1 items-center justify-center gap-2 border border-[#C59B27] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#C59B27] transition-colors hover:bg-[#C59B27] hover:text-[#121110] disabled:opacity-50"
                >
                  <Upload size={11} /> {uploadingId === f.fabric_id ? "Uploading…" : "Photo"}
                </button>
                <button
                  data-testid={`admin-fabric-toggle-${f.fabric_id}`}
                  onClick={() => toggleActive(f)}
                  className="border border-[#2A2825] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#858078] transition-colors hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
                >
                  {f.active ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StudioVideoPanel() {
  const [settings, setSettings] = useState({ video_url: "", has_upload: false });
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/studio-settings");
      setSettings(data);
      setLink(data.video_url || "");
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLink = async () => {
    try {
      const { data } = await api.put("/admin/studio-settings", { video_url: link.trim() });
      setSettings(data);
      toast.success(link.trim() ? "Measurement video link saved." : "Video removed — placeholder is showing.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/studio-video", fd);
      setSettings(data);
      setLink("");
      toast.success("Measurement video uploaded and live on the Design Studio.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const status = settings.has_upload
    ? "Your uploaded video is live"
    : settings.video_url
      ? "Your video link is live"
      : "No video yet — clients see an elegant placeholder";

  return (
    <div className="border border-[#2A2825] bg-[#1A1917] p-6" data-testid="admin-studio-video-panel">
      <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideoFile} data-testid="admin-studio-video-input" />
      <p className="font-serif text-lg text-[#FFFFFF]">Measurement Process Video</p>
      <p className="mt-1 max-w-2xl text-sm font-light leading-relaxed text-[#858078]">
        Shown in the "From cloth to closet" section of the Design Studio. Paste a YouTube or Vimeo
        link, or upload a video file (up to 80 MB). {" "}
        <span className="text-[#C59B27]" data-testid="admin-studio-video-status">{status}.</span>
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="admin-studio-video-link-input"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=…"
          className="min-w-[280px] flex-1 border border-[#2A2825] bg-[#121110] px-4 py-3 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]"
        />
        <button
          data-testid="admin-studio-video-save-link"
          onClick={saveLink}
          className="border border-[#C59B27] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C59B27] transition-colors hover:bg-[#C59B27] hover:text-[#121110]"
        >
          Save Link
        </button>
        <button
          data-testid="admin-studio-video-upload"
          onClick={() => videoRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-[#C59B27] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#121110] transition-colors hover:bg-[#FFFFFF] disabled:opacity-50"
        >
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload Video"}
        </button>
      </div>
    </div>
  );
}
